if (!customElements.get('cookie-banner')) {
  customElements.define(
    'cookie-banner',
    class CookieBanner extends ModalElement {
      constructor() {
        super();

        this.decisionRecorded = false;
        this.consentReady = false;
        this.showFallback = null;
        this.focusRetry = null;
        this.init();
      }

      // No usamos el bloqueo de la clase base: se apoya en un contador compartido con
      // el resto de modales del tema que exige emparejar cada afterShow con un
      // afterHide. Las promesas de transicion de este dialogo no lo garantizan (por
      // eso ambas llevan red de seguridad), y un contador descuadrado no se recupera:
      // dejaria la tienda entera sin scroll. Aqui el bloqueo lo damos con una clase
      // propia, que es idempotente.
      get shouldLock() {
        return false;
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get privacy() {
        return window.Shopify && window.Shopify.customerPrivacy;
      }

      get acceptButton() {
        return this.querySelector('button[name="accept"]');
      }

      get declineButton() {
        return this.querySelector('button[name="decline"]');
      }

      get customizeButton() {
        return this.querySelector('button[name="customize"]');
      }

      get saveButton() {
        return this.querySelector('button[name="save"]');
      }

      get closeButton() {
        return this.querySelector('.drawer__close');
      }

      get noticeHeading() {
        return this.querySelector('.cookie__notice [id^="Dialog-"]');
      }

      get preferencesHeading() {
        return this.querySelector('.cookie__preferences-heading');
      }

      // El texto del aviso enlaza a la politica de privacidad. Si el dialogo bloqueante
      // se autoabriera tambien ahi, taparia justo la pagina que el visitante necesita
      // leer antes de decidir. Se suprime solo la apertura automatica: el enlace del
      // footer sigue cableado y funcionando en estas paginas.
      get isPolicyPage() {
        return window.location.pathname.includes('/policies/');
      }

      // Solo los interruptores conmutables: el de "Notwendig" va disabled
      get toggles() {
        return Array.from(this.querySelectorAll('input[data-category]'));
      }

      // Sin gesture-element, el primer foco va al primer botón de la vista visible.
      // Tiene que mirar data-view: al reabrir desde el footer, los botones del
      // aviso están en display:none y no se les puede dar foco.
      get focusElement() {
        const view = this.getAttribute('data-view') === 'preferences' ? '.cookie__preferences' : '.cookie__notice';

        return this.querySelector(`${view} button`) || this.querySelector('button');
      }

      // Una decisión existe si ya la registramos en esta carga o si la API la reporta
      get hasDecision() {
        if (this.decisionRecorded) return true;

        const consent = this.privacy && this.privacy.getTrackingConsent();
        return Boolean(consent) && consent !== 'no_interaction';
      }

      init() {
        // En el editor no se consulta la API: solo se permite previsualizar ambas vistas
        if (Shopify.designMode) {
          this.bindViewEvents();
          this.consentReady = true;
          return;
        }

        window.Shopify.loadFeatures([{
          name: 'consent-tracking-api',
          version: '0.1',
          onLoad: this.onConsentLibraryLoaded.bind(this)
        }]);
      }

      // Si la API no carga no se muestra nada: sin ella no se puede registrar la
      // elección, y sin consentimiento tampoco se dispara tracking. Falla cerrado.
      onConsentLibraryLoaded(error) {
        if (error || !this.privacy) return;

        this.bindViewEvents();
        this.bindConsentEvents();
        this.consentReady = true;

        if (!this.isPolicyPage && (this.testMode || !this.hasDecision)) {
          this.show();
        }
      }

      // Sin la Consent API lista no hay ningun boton cableado ni forma de cerrar:
      // abrir el dialogo aqui dejaria al visitante atrapado tras un overlay opaco.
      show(activeElement = null, animate = true) {
        if (!this.consentReady) return;

        return super.show(activeElement, animate);
      }

      bindViewEvents() {
        if (this.customizeButton) {
          this.customizeButton.addEventListener('click', () => this.setView('preferences'));
        }
      }

      bindConsentEvents() {
        if (this.acceptButton) {
          this.acceptButton.addEventListener('click', () => this.save({ analytics: true, marketing: true }));
        }
        if (this.declineButton) {
          this.declineButton.addEventListener('click', () => this.save({ analytics: false, marketing: false }));
        }
        if (this.saveButton) {
          this.saveButton.addEventListener('click', () => this.save(this.readToggles()));
        }
        if (this.closeButton) {
          this.closeButton.addEventListener('click', () => this.hide());
        }
      }

      setView(view) {
        this.setAttribute('data-view', view);
        this.syncFocusables();

        const heading = view === 'preferences' ? this.preferencesHeading : this.noticeHeading;
        if (heading) this.setAttribute('aria-labelledby', heading.id);

        const target = view === 'preferences' ? this.saveButton : this.acceptButton;
        if (target) target.focus();

        // El conjunto de enfocables acaba de cambiar: hay que rearmar la trampa.
        theme.a11y.trapFocus(this, target);
      }

      // El selector de foco del tema no descarta lo oculto por display:none, asi que
      // todo lo enfocable de la vista inactiva seguiria contando como primero o ultimo
      // y el manejador de Tab nunca se armaria. Los controles de formulario se
      // descartan con disabled; los enlaces, que no admiten disabled, retirandoles el href.
      syncFocusables() {
        const preferences = this.getAttribute('data-view') === 'preferences';

        if (this.closeButton) this.closeButton.disabled = !this.classList.contains('is-dismissable');
        if (this.saveButton) this.saveButton.disabled = !preferences;

        [this.acceptButton, this.declineButton, this.customizeButton].forEach((button) => {
          if (button) button.disabled = preferences;
        });

        this.toggles.forEach((input) => {
          input.disabled = !preferences;
        });

        // A los enlaces no les basta tabindex="-1": el selector de foco del tema
        // incluye `a[href]` de forma incondicional, asi que un ancla oculta seguiria
        // contando como primer enfocable. Se le retira el href y se guarda para
        // restaurarlo cuando su vista vuelva a estar visible.
        this.querySelectorAll('.cookie__notice a, .cookie__preferences a').forEach((link) => {
          const oculto = link.closest('.cookie__preferences') ? !preferences : preferences;

          if (oculto) {
            if (link.hasAttribute('href')) {
              link.dataset.href = link.getAttribute('href');
              link.removeAttribute('href');
            }
          }
          else if (link.dataset.href) {
            link.setAttribute('href', link.dataset.href);
            delete link.dataset.href;
          }
        });
      }

      readToggles() {
        return this.toggles.reduce((consent, input) => {
          consent[input.dataset.category] = input.checked;
          return consent;
        }, { analytics: false, marketing: false });
      }

      syncToggles() {
        const consent = (this.privacy && this.privacy.currentVisitorConsent()) || {};

        this.toggles.forEach((input) => {
          input.checked = consent[input.dataset.category] === 'yes';
        });
      }

      // sale_of_data sigue a marketing; preferences es funcional y va siempre activo
      save(categories) {
        this.decisionRecorded = true;

        if (this.privacy) {
          this.privacy.setTrackingConsent({
            analytics: categories.analytics,
            marketing: categories.marketing,
            sale_of_data: categories.marketing,
            preferences: true
          }, () => {});
        }

        this.hide();
      }

      // Antes de haber decidido, el diálogo no se cierra por ninguna vía:
      // ni Escape, ni clic en el overlay, ni el evento modal:closeAll
      hide() {
        if (!Shopify.designMode && !this.hasDecision) return;

        return super.hide();
      }

      // Mismo problema que en hideTransition y agravado por el :not([hidden]) del
      // CSS: al pasar de display:none a flex el navegador no interpola la primera
      // transicion del overlay, asi que no llega ningun transitionend y la promesa
      // de la clase base queda colgada. Sin que resuelva no corre afterShow(), y sin
      // afterShow no hay trampa de foco ni bloqueo de scroll: el dialogo dejaria de
      // ser bloqueante. Red de seguridad por tiempo, igual que en el cierre.
      showTransition(animate = true) {
        const base = super.showTransition(animate);

        return new Promise((resolve) => {
          const done = () => {
            clearTimeout(this.showFallback);
            this.showFallback = null;
            resolve();
          };

          this.showFallback = setTimeout(done, 1000);
          base.then(done);
        });
      }

      // ModalElement.hideTransition() espera un transitionend del overlay que en este
      // dialogo no llega, asi que afterHide() nunca correria y el body se quedaria con
      // has-modal-open y has-cookie-banner. En tactil ambas aplican touch-action: none
      // y la tienda deja de poder desplazarse con el dedo. Red de seguridad por tiempo.
      hideTransition() {
        this.removeAttribute('active');

        return new Promise((resolve) => {
          const done = () => {
            clearTimeout(this.hideFallback);
            this.hideFallback = null;
            this.overlay.removeEventListener('transitionend', done);
            resolve();
          };
          this.hideFallback = setTimeout(done, 1000);

          this.overlay.addEventListener('transitionend', done);
        });
      }

      // Reabrir desde el footer entra directo al panel y permite cerrar
      beforeShow() {
        // Si quedaba un cierre a medias, cancela su respaldo: ya no hace falta
        // cuadrar ningun contador compartido, con shouldLock en false el bloqueo
        // lo da la clase has-cookie-banner, que es idempotente.
        clearTimeout(this.hideFallback);
        this.hideFallback = null;

        const reopened = this.hasDecision;

        this.classList.toggle('is-dismissable', reopened);

        if (reopened) {
          this.syncToggles();
          this.setAttribute('data-view', 'preferences');
        } else {
          this.setAttribute('data-view', 'notice');
        }

        // Reabrir desde el footer fija data-view aqui mismo, sin pasar por setView():
        // si nunca se visito el panel de preferencias, aria-labelledby seguiria
        // apuntando al encabezado del aviso, oculto en esta vista.
        const heading = reopened ? this.preferencesHeading : this.noticeHeading;
        if (heading) this.setAttribute('aria-labelledby', heading.id);

        this.syncFocusables();
      }

      afterShow() {
        super.afterShow();
        document.body.classList.add('has-cookie-banner');
        this.focusWhenVisible();
      }

      // afterShow puede correr por la red de seguridad mientras el contenido sigue
      // en visibility:hidden por la transicion del tema, y un elemento invisible no
      // admite foco: el .focus() de trapFocus se pierde en silencio y la trampa de
      // Tab, que se arma cuando el foco entra, no llega a activarse. Se reintenta
      // hasta que el elemento acepte el foco, con tope para no quedarse en bucle.
      focusWhenVisible(attempts = 15) {
        if (this.contains(document.activeElement)) return;
        if (attempts <= 0 || !this.open) return;

        const target = this.focusElement;
        if (target) target.focus();

        if (!this.contains(document.activeElement)) {
          this.focusRetry = setTimeout(() => this.focusWhenVisible(attempts - 1), 100);
        }
      }

      afterHide() {
        // Si se reabrio mientras el respaldo estaba en vuelo, no desmontar nada.
        if (this.open) return;

        clearTimeout(this.focusRetry);

        super.afterHide();
        document.body.classList.remove('has-cookie-banner');
      }
    }
  );
}
